import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    trn: '',
    address: '',
    city: '',
    emirate: '',
    country: 'UAE',
    contactPerson: '',
    creditLimit: 0,
    paymentTerms: 'Net 30',
    customerType: 'Individual',
    status: 'Active'
  });

  const [errors, setErrors] = useState({});

  // UAE Emirates list
  const emirates = ['Abu Dhabi', 'Dubai', 'Sharjah', 'Ajman', 'Umm Al Quwain', 'Ras Al Khaimah', 'Fujairah'];

  // Sample customer data
  const sampleCustomers = [
    {
      id: 1,
      name: 'Al Noor Trading LLC',
      email: 'info@alnoortrading.ae',
      phone: '+971-4-1234567',
      trn: '100123456789001',
      address: 'Office 501, Business Bay Tower, Dubai',
      city: 'Dubai',
      emirate: 'Dubai',
      country: 'UAE',
      contactPerson: 'Ahmed Al Noor',
      creditLimit: 50000,
      paymentTerms: 'Net 30',
      customerType: 'Business',
      status: 'Active',
      createdAt: '2026-01-15',
      totalInvoices: 12,
      outstandingAmount: 25000
    },
    {
      id: 2,
      name: 'Gulf Construction Co.',
      email: 'projects@gulfconstruction.ae',
      phone: '+971-2-7654321',
      trn: '100234567890002',
      address: 'Building 123, Industrial Area, Abu Dhabi',
      city: 'Abu Dhabi',
      emirate: 'Abu Dhabi',
      country: 'UAE',
      contactPerson: 'Mohammed Al Mansoori',
      creditLimit: 100000,
      paymentTerms: 'Net 45',
      customerType: 'Business',
      status: 'Active',
      createdAt: '2026-02-01',
      totalInvoices: 8,
      outstandingAmount: 45000
    },
    {
      id: 3,
      name: 'Sarah Al Maktoum',
      email: 'sarah.almaktoum@email.ae',
      phone: '+971-50-9876543',
      trn: '',
      address: 'Villa 45, Jumeirah Beach Residence, Dubai',
      city: 'Dubai',
      emirate: 'Dubai',
      country: 'UAE',
      contactPerson: 'Sarah Al Maktoum',
      creditLimit: 10000,
      paymentTerms: 'Net 15',
      customerType: 'Individual',
      status: 'Active',
      createdAt: '2026-02-20',
      totalInvoices: 3,
      outstandingAmount: 5000
    },
    {
      id: 4,
      name: 'Tech Solutions FZE',
      email: 'contact@techsolutions.ae',
      phone: '+971-6-1122334',
      trn: '100345678901003',
      address: 'Warehouse 7, Sharjah Airport Free Zone',
      city: 'Sharjah',
      emirate: 'Sharjah',
      country: 'UAE',
      contactPerson: 'Fatima Al Zaabi',
      creditLimit: 75000,
      paymentTerms: 'Net 30',
      customerType: 'Business',
      status: 'Active',
      createdAt: '2026-03-01',
      totalInvoices: 5,
      outstandingAmount: 15000
    }
  ];

  // Initialize with sample data
  useEffect(() => {
    setCustomers(sampleCustomers);
  }, []);

  // Filter customers based on search term
  const filteredCustomers = customers.filter(customer =>
    customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.phone.includes(searchTerm) ||
    customer.trn.includes(searchTerm)
  );

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Customer name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';

    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';

    if (formData.customerType === 'Business' && !formData.trn.trim()) {
      newErrors.trn = 'TRN is required for business customers';
    }

    if (formData.trn && formData.trn.length !== 15) {
      newErrors.trn = 'TRN must be 15 digits';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    if (editingCustomer) {
      // Update existing customer
      setCustomers(prev => prev.map(customer =>
        customer.id === editingCustomer.id
          ? { ...customer, ...formData, updatedAt: new Date().toISOString().split('T')[0] }
          : customer
      ));
      setEditingCustomer(null);
    } else {
      // Add new customer
      const newCustomer = {
        ...formData,
        id: Math.max(...customers.map(c => c.id)) + 1,
        createdAt: new Date().toISOString().split('T')[0],
        totalInvoices: 0,
        outstandingAmount: 0
      };
      setCustomers(prev => [...prev, newCustomer]);
    }

    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      trn: '',
      address: '',
      city: '',
      emirate: '',
      country: 'UAE',
      contactPerson: '',
      creditLimit: 0,
      paymentTerms: 'Net 30',
      customerType: 'Individual',
      status: 'Active'
    });

    setShowAddForm(false);
  };

  // Handle edit customer
  const handleEdit = (customer) => {
    setFormData(customer);
    setEditingCustomer(customer);
    setShowAddForm(true);
  };

  // Handle delete customer
  const handleDelete = (customerId) => {
    if (window.confirm('Are you sure you want to delete this customer?')) {
      setCustomers(prev => prev.filter(customer => customer.id !== customerId));
    }
  };

  // Cancel form
  const handleCancel = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      trn: '',
      address: '',
      city: '',
      emirate: '',
      country: 'UAE',
      contactPerson: '',
      creditLimit: 0,
      paymentTerms: 'Net 30',
      customerType: 'Individual',
      status: 'Active'
    });
    setEditingCustomer(null);
    setShowAddForm(false);
    setErrors({});
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5 p-3 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary via-secondary to-accent rounded-t-2xl p-4 sm:p-8 text-white shadow-xl">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex-1">
              <h1 className="text-2xl sm:text-4xl font-bold mb-1 sm:mb-2">Customer Management</h1>
              <p className="text-xs sm:text-base text-white/80">Manage your customer database and relationships</p>
              <div className="mt-3 sm:mt-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 text-sm sm:text-base">
                <div className="flex items-center gap-2">
                  <span className="text-lg sm:text-2xl">🇦🇪</span>
                  <span>UAE Compliant</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-lg">📊</span>
                  <span>{customers.length} Customers</span>
                </div>
              </div>
            </div>
            <div className="badge badge-lg bg-white/20 text-white border-0 p-2 sm:p-4 shrink-0">
              <span className="text-lg sm:text-2xl font-bold">👥</span>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-base-100 rounded-b-2xl shadow-2xl p-4 sm:p-8">
          {/* Action Bar */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center space-x-4">
              <div className="form-control">
                <div className="input-group">
                  <input
                    type="text"
                    placeholder="Search customers..."
                    className="input input-bordered"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <button className="btn btn-square">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            <button
              className="btn btn-primary btn-lg hover:scale-105 transition-transform shadow-lg"
              onClick={() => setShowAddForm(true)}
            >
              ➕ Add Customer
            </button>
          </div>

          {/* Add/Edit Customer Form */}
          {showAddForm && (
            <div className="bg-gradient-to-r from-primary/10 to-secondary/10 p-4 sm:p-6 rounded-xl mb-6">
              <h3 className="text-lg sm:text-xl font-bold text-primary mb-4 flex items-center gap-2">
                <span className="w-1 h-6 bg-primary rounded-full"></span>
                {editingCustomer ? 'Edit Customer' : 'Add New Customer'}
              </h3>

              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                {/* Basic Information */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-semibold">Customer Name *</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      className={`input input-bordered border-2 ${errors.name ? 'input-error' : ''}`}
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter customer name"
                    />
                    {errors.name && <span className="text-error text-sm mt-1">{errors.name}</span>}
                  </div>

                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-semibold">Email *</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      className={`input input-bordered border-2 ${errors.email ? 'input-error' : ''}`}
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="customer@example.com"
                    />
                    {errors.email && <span className="text-error text-sm mt-1">{errors.email}</span>}
                  </div>

                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-semibold">Phone *</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      className={`input input-bordered border-2 ${errors.phone ? 'input-error' : ''}`}
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+971-50-1234567"
                    />
                    {errors.phone && <span className="text-error text-sm mt-1">{errors.phone}</span>}
                  </div>

                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-semibold">TRN (Tax Registration)</span>
                    </label>
                    <input
                      type="text"
                      name="trn"
                      className={`input input-bordered border-2 font-mono ${errors.trn ? 'input-error' : ''}`}
                      value={formData.trn}
                      onChange={handleInputChange}
                      placeholder="15-digit TRN"
                      maxLength="15"
                    />
                    {errors.trn && <span className="text-error text-sm mt-1">{errors.trn}</span>}
                  </div>

                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-semibold">Customer Type</span>
                    </label>
                    <select
                      name="customerType"
                      className="select select-bordered border-2"
                      value={formData.customerType}
                      onChange={handleInputChange}
                    >
                      <option value="Individual">Individual</option>
                      <option value="Business">Business</option>
                    </select>
                  </div>

                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-semibold">Contact Person</span>
                    </label>
                    <input
                      type="text"
                      name="contactPerson"
                      className="input input-bordered border-2"
                      value={formData.contactPerson}
                      onChange={handleInputChange}
                      placeholder="Primary contact person"
                    />
                  </div>
                </div>

                {/* Address Information */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-semibold">Address</span>
                    </label>
                    <textarea
                      name="address"
                      className="textarea textarea-bordered border-2 h-20"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="Street address, building, etc."
                    ></textarea>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div className="form-control">
                      <label className="label">
                        <span className="label-text font-semibold">City</span>
                      </label>
                      <input
                        type="text"
                        name="city"
                        className="input input-bordered border-2"
                        value={formData.city}
                        onChange={handleInputChange}
                        placeholder="City"
                      />
                    </div>

                    <div className="form-control">
                      <label className="label">
                        <span className="label-text font-semibold">Emirate</span>
                      </label>
                      <select
                        name="emirate"
                        className="select select-bordered border-2"
                        value={formData.emirate}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Emirate</option>
                        {emirates.map(emirate => (
                          <option key={emirate} value={emirate}>{emirate}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Financial Information */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-semibold">Credit Limit (AED)</span>
                    </label>
                    <input
                      type="number"
                      name="creditLimit"
                      className="input input-bordered border-2"
                      value={formData.creditLimit}
                      onChange={handleInputChange}
                      min="0"
                      step="1000"
                      placeholder="0"
                    />
                  </div>

                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-semibold">Payment Terms</span>
                    </label>
                    <select
                      name="paymentTerms"
                      className="select select-bordered border-2"
                      value={formData.paymentTerms}
                      onChange={handleInputChange}
                    >
                      <option value="Cash">Cash</option>
                      <option value="Net 15">Net 15</option>
                      <option value="Net 30">Net 30</option>
                      <option value="Net 45">Net 45</option>
                      <option value="Net 60">Net 60</option>
                    </select>
                  </div>

                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-semibold">Status</span>
                    </label>
                    <select
                      name="status"
                      className="select select-bordered border-2"
                      value={formData.status}
                      onChange={handleInputChange}
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                      <option value="Suspended">Suspended</option>
                    </select>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4 pt-4 border-t">
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm sm:btn-lg hover:bg-base-200 order-2 sm:order-1"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary btn-sm sm:btn-lg bg-gradient-to-r from-primary to-secondary border-0 hover:scale-105 transition-transform shadow-lg order-1 sm:order-2"
                  >
                    {editingCustomer ? '💾 Update Customer' : '💾 Save Customer'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Customers Table - Desktop View */}
          <div className="bg-base-100 rounded-xl shadow-lg overflow-hidden hidden md:block">
            <div className="overflow-x-auto">
              <table className="table table-zebra w-full">
                <thead className="bg-gradient-to-r from-primary to-secondary text-white">
                  <tr>
                    <th className="rounded-tl-lg">Customer</th>
                    <th>Contact</th>
                    <th>TRN</th>
                    <th>Type</th>
                    <th>Credit Limit</th>
                    <th>Outstanding</th>
                    <th>Status</th>
                    <th className="rounded-tr-lg">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCustomers.map((customer) => (
                    <tr key={customer.id} className="hover:bg-base-200 transition-colors">
                      <td>
                        <div>
                          <div className="font-bold text-primary">{customer.name}</div>
                          <div className="text-sm text-gray-500">{customer.contactPerson}</div>
                          <div className="text-xs text-gray-400">{customer.createdAt}</div>
                        </div>
                      </td>
                      <td>
                        <div className="text-sm">
                          <div>{customer.email}</div>
                          <div className="text-gray-500">{customer.phone}</div>
                        </div>
                      </td>
                      <td>
                        <span className="font-mono text-sm">
                          {customer.trn || 'N/A'}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${
                          customer.customerType === 'Business'
                            ? 'badge-primary'
                            : 'badge-secondary'
                        }`}>
                          {customer.customerType}
                        </span>
                      </td>
                      <td className="font-mono">
                        {formatCurrency(customer.creditLimit)}
                      </td>
                      <td className="font-mono">
                        <span className={customer.outstandingAmount > customer.creditLimit * 0.8 ? 'text-error' : 'text-success'}>
                          {formatCurrency(customer.outstandingAmount)}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${
                          customer.status === 'Active'
                            ? 'badge-success'
                            : customer.status === 'Inactive'
                            ? 'badge-warning'
                            : 'badge-error'
                        }`}>
                          {customer.status}
                        </span>
                      </td>
                      <td>
                        <div className="flex gap-2">
                          <button
                            className="btn btn-xs btn-outline btn-info"
                            onClick={() => handleEdit(customer)}
                          >
                            ✏️
                          </button>
                          <button
                            className="btn btn-xs btn-outline btn-error"
                            onClick={() => handleDelete(customer.id)}
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredCustomers.length === 0 && (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">👥</div>
                <h3 className="text-xl font-bold text-gray-600 mb-2">No customers found</h3>
                <p className="text-gray-500 mb-4">
                  {searchTerm ? 'Try adjusting your search terms' : 'Start by adding your first customer'}
                </p>
                {!searchTerm && (
                  <button
                    className="btn btn-primary"
                    onClick={() => setShowAddForm(true)}
                  >
                    Add Your First Customer
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Customers Cards - Mobile View */}
          <div className="md:hidden space-y-3">
            {filteredCustomers.length === 0 ? (
              <div className="bg-base-200 rounded-xl text-center py-12">
                <div className="text-5xl mb-4">👥</div>
                <h3 className="text-lg font-bold text-gray-600 mb-2">No customers found</h3>
                <p className="text-sm text-gray-500 mb-4">
                  {searchTerm ? 'Try adjusting your search terms' : 'Start by adding your first customer'}
                </p>
                {!searchTerm && (
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => setShowAddForm(true)}
                  >
                    Add Your First Customer
                  </button>
                )}
              </div>
            ) : (
              filteredCustomers.map((customer) => (
                <div key={customer.id} className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <p className="font-bold text-primary text-sm">{customer.name}</p>
                      <p className="text-xs text-gray-500">{customer.contactPerson}</p>
                      <p className="text-xs text-gray-400 mt-1">{customer.createdAt}</p>
                    </div>
                    <span className={`badge badge-sm ${
                      customer.status === 'Active'
                        ? 'badge-success'
                        : customer.status === 'Inactive'
                        ? 'badge-warning'
                        : 'badge-error'
                    }`}>
                      {customer.status}
                    </span>
                  </div>
                  
                  <div className="space-y-2 mb-3 text-xs">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <p className="text-gray-500 text-xs">Email</p>
                        <p className="font-medium">{customer.email}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Phone</p>
                        <p className="font-medium">{customer.phone}</p>
                      </div>
                    </div>
                    {customer.trn && (
                      <div>
                        <p className="text-gray-500 text-xs">TRN</p>
                        <p className="font-mono text-xs">{customer.trn}</p>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-2 pt-1 border-t">
                      <div>
                        <p className="text-gray-500 text-xs">Credit Limit</p>
                        <p className="font-mono font-semibold">{formatCurrency(customer.creditLimit)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Outstanding</p>
                        <p className={`font-mono font-semibold ${customer.outstandingAmount > customer.creditLimit * 0.8 ? 'text-error' : 'text-success'}`}>
                          {formatCurrency(customer.outstandingAmount)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      className="btn btn-sm btn-outline btn-info flex-1"
                      onClick={() => handleEdit(customer)}
                    >
                      ✏️ Edit
                    </button>
                    <button
                      className="btn btn-sm btn-outline btn-error flex-1"
                      onClick={() => handleDelete(customer.id)}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mt-6">
            <div className="stat bg-gradient-to-br from-primary/20 to-primary/5 rounded-xl p-3 sm:p-6">
              <div className="stat-figure text-primary text-lg sm:text-2xl">
                <span>👥</span>
              </div>
              <div className="stat-title text-xs sm:text-sm">Total Customers</div>
              <div className="stat-value text-primary text-lg sm:text-2xl">{customers.length}</div>
            </div>

            <div className="stat bg-gradient-to-br from-success/20 to-success/5 rounded-xl p-3 sm:p-6">
              <div className="stat-figure text-success text-lg sm:text-2xl">
                <span>💰</span>
              </div>
              <div className="stat-title text-xs sm:text-sm">Total Credit Limit</div>
              <div className="stat-value text-success text-lg sm:text-2xl">
                {formatCurrency(customers.reduce((sum, c) => sum + c.creditLimit, 0))}
              </div>
            </div>

            <div className="stat bg-gradient-to-br from-warning/20 to-warning/5 rounded-xl p-3 sm:p-6">
              <div className="stat-figure text-warning text-lg sm:text-2xl">
                <span>📊</span>
              </div>
              <div className="stat-title text-xs sm:text-sm">Outstanding Amount</div>
              <div className="stat-value text-warning text-lg sm:text-2xl">
                {formatCurrency(customers.reduce((sum, c) => sum + c.outstandingAmount, 0))}
              </div>
            </div>

            <div className="stat bg-gradient-to-br from-info/20 to-info/5 rounded-xl p-3 sm:p-6">
              <div className="stat-figure text-info text-lg sm:text-2xl">
                <span>📄</span>
              </div>
              <div className="stat-title text-xs sm:text-sm">Active Customers</div>
              <div className="stat-value text-info text-lg sm:text-2xl">
                {customers.filter(c => c.status === 'Active').length}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-xs sm:text-sm text-base-500 mt-4">
          <p>🇦🇪 UAE Federal Tax Authority Compliant | Customer Management System</p>
        </div>
      </div>
    </div>
  );
};

export default Customers;